import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Checkbox, FileInput, TextArea } from 'common-src/components/base';
import { getCurrentUserId } from 'common-src/features/auth/selectors';
import { cachePortalThreadMessages, cachePortalThreads } from 'common-src/features/cache';
import { apiRequest } from 'common-src/features/rest';
import { getPromptTemplatesRequest } from 'common-src/models/PromptTemplate';
import { getUniqueClientPromptTemplates } from 'common-src/models/PromptTemplate/selectors';
import { ColorsNew } from 'common-src/styles';

import useCustomSelector from 'src/hooks/useCustomSelector';
import usePopup from 'src/hooks/usePopup';
import BasePopup from 'src/popups/BasePopup';
import {
  getNormalizedThreadMessages,
  getNormalizedThreadsFromMessages,
  sendSecureMessage,
} from 'src/utils/secureMessagingUtils';

import { NotificationTypeEnum, ReceiverTypeEnum } from './constants';
import styles from './SendSecureMessagePopup.module.scss';

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const getNotificationType = ({ email, sms }) => {
  if (email && sms) return NotificationTypeEnum.BOTH;
  if (sms) return NotificationTypeEnum.SMS;
  return NotificationTypeEnum.EMAIL;
};

const initialState = {
  subject: '',
  body: '',
  attachment: null,
  recipients: {
    patient: true,
    caregiver: false,
  },
  sendVia: {
    email: true,
    sms: false,
  },
};

const SendSecureMessagePopup = ({ patientId, onClose }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState(initialState);
  const currentUserId = useCustomSelector((state) => getCurrentUserId(state));
  const promptTemplates = useCustomSelector((state) => getUniqueClientPromptTemplates(state));

  useEffect(() => {
    dispatch(getPromptTemplatesRequest());
  }, [dispatch]);

  const { errors, setErrors, renderButtons } = usePopup();

  const hasRecipientSelected = useMemo(
    () => data.recipients.patient || data.recipients.caregiver,
    [data.recipients.patient, data.recipients.caregiver],
  );
  const hasSendViaSelected = useMemo(
    () => data.sendVia.email || data.sendVia.sms,
    [data.sendVia.email, data.sendVia.sms],
  );

  const setRecipient = (key, value) => {
    setData((prev) => ({
      ...prev,
      recipients: { ...prev.recipients, [key]: value },
    }));

    const updatedRecipients = { ...data.recipients, [key]: value };
    setErrors((prev) => ({
      ...prev,
      recipients:
        updatedRecipients.patient || updatedRecipients.caregiver
          ? ''
          : 'Select at least one recipient',
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setData((prev) => ({ ...prev, attachment: null }));
      setErrors((prev) => ({ ...prev, attachment: '' }));
      return;
    }

    setData((prev) => ({ ...prev, attachment: file }));
    setErrors((prev) => ({
      ...prev,
      attachment: file.size > MAX_FILE_SIZE ? 'File size must be less than 10MB' : '',
    }));
  };

  const setSendVia = (key, value) => {
    setData((prev) => ({
      ...prev,
      sendVia: { ...prev.sendVia, [key]: value },
    }));

    const updatedSendVia = { ...data.sendVia, [key]: value };
    setErrors((prev) => ({
      ...prev,
      sendVia: updatedSendVia.email || updatedSendVia.sms ? '' : 'Select at least one notify type',
    }));
  };

  const onSubmit = async (loadingCallback, successCallback, errorCallback) => {
    if (!patientId) {
      errorCallback('Patient is required before sending a secure message.');
      return;
    }

    if (!hasRecipientSelected) {
      setErrors((prev) => ({ ...prev, recipients: 'Select at least one recipient' }));
      return;
    }
    if (!hasSendViaSelected) {
      setErrors((prev) => ({ ...prev, sendVia: 'Select at least one notify type' }));
      return;
    }

    loadingCallback();

    try {
      const createdMessage = await sendSecureMessage({
        patientId,
        messageSubject: data.subject.trim(),
        body: data.body.trim(),
        sendToPatient: data.recipients.patient,
        sendToCaregiver: data.recipients.caregiver,
        receiverType: ReceiverTypeEnum.PATIENT,
        notificationType: getNotificationType(data.sendVia),
        attachment: data.attachment,
      });

      const refreshResponse = await apiRequest({
        endpoint: `patientPortalMessages/patient/${patientId}`,
      });
      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();
        const payload =
          refreshData?.PatientPortalMessage ??
          refreshData?.patientPortalMessage ??
          refreshData?.patient_portal_message ??
          [];
        const items = Array.isArray(payload) ? payload : payload ? [payload] : [];
        dispatch(
          cachePortalThreads(patientId, getNormalizedThreadsFromMessages(items, currentUserId)),
        );
      }

      const messagePayload =
        createdMessage?.PatientPortalMessage ??
        createdMessage?.patientPortalMessage ??
        createdMessage?.patient_portal_message ??
        null;
      const sentThreadId = messagePayload?.threadId ?? messagePayload?.thread_id ?? null;
      if (sentThreadId) {
        const threadResponse = await apiRequest({
          endpoint: `patientPortalMessages/thread/${sentThreadId}`,
        });
        if (threadResponse.ok) {
          const threadData = await threadResponse.json();
          const threadPayload =
            threadData?.PatientPortalMessage ??
            threadData?.patientPortalMessage ??
            threadData?.patient_portal_message ??
            [];
          const threadItems = Array.isArray(threadPayload)
            ? threadPayload
            : threadPayload
              ? [threadPayload]
              : [];
          dispatch(
            cachePortalThreadMessages(sentThreadId, getNormalizedThreadMessages(threadItems)),
          );
        }
      }

      successCallback('Message sent successfully!');
      onClose(true);
    } catch (error) {
      errorCallback(error?.message || 'Failed to send message');
    }
  };

  return (
    <BasePopup id="send-secure-message" open onClose={onClose} title="Send  Message">
      <div className={styles.content}>
        <TextArea
          id="secure-message-subject"
          type="template"
          label="Subject"
          placeholder="Enter subject"
          value={data.subject}
          onTextChange={(value) => setData((prev) => ({ ...prev, subject: value }))}
          templates={promptTemplates}
          maxRows={2}
          fixedRows={1}
          required
          requiredColor={ColorsNew.mediumDarkRed}
        />
        <TextArea
          id="secure-message-body"
          type="template"
          label="Body"
          placeholder="Enter message"
          value={data.body}
          onTextChange={(value) => setData((prev) => ({ ...prev, body: value }))}
          templates={promptTemplates}
          fixedRows={5}
          required
        />
        <p className={[styles.label, 'font-w-500', 'font-s-14'].join(' ')}>Attachment</p>
        <FileInput
          id="secure-message-attachment"
          inputAccept="*"
          placeholder="Click to select attachment"
          description="Optional, max 10MB"
          value={data.attachment}
          errorText={errors.attachment}
          handleFileChange={handleFileChange}
        />

        <div className={styles.recipients}>
          <p className={[styles.label, 'font-w-500', 'font-s-14'].join(' ')}>
            Recipients<span className={styles.requiredStar}>*</span>
          </p>
          <div className={['flex-row', 'gap-20'].join(' ')}>
            <Checkbox
              id="recipient-patient"
              label="Patient"
              checked={data.recipients.patient}
              onChange={(value) => setRecipient('patient', value)}
            />
            <Checkbox
              id="recipient-caregiver"
              label="Caregiver"
              checked={data.recipients.caregiver}
              onChange={(value) => setRecipient('caregiver', value)}
            />
          </div>
          <p className={[styles.error, 'font-s-12'].join(' ')}>{errors.recipients}</p>
        </div>
        <div className={styles.recipients}>
          <p className={[styles.label, 'font-w-500', 'font-s-14'].join(' ')}>
            Notify via<span className={styles.requiredStar}>*</span>
          </p>
          <div className={['flex-row', 'gap-20'].join(' ')}>
            <Checkbox
              id="send-via-email"
              label="Email"
              checked={data.sendVia.email}
              onChange={(value) => setSendVia('email', value)}
            />
            <Checkbox
              id="send-via-sms"
              label="SMS"
              checked={data.sendVia.sms}
              onChange={(value) => setSendVia('sms', value)}
              disabled
            />
          </div>
          <p className={[styles.error, 'font-s-12'].join(' ')}>{errors.sendVia}</p>
        </div>
      </div>
      {renderButtons({
        containerClassName: styles.buttonsContainer,
        onClose,
        onSubmit,
        isSubmitEnabled:
          !!data.subject.trim() &&
          !!data.body.trim() &&
          hasRecipientSelected &&
          hasSendViaSelected &&
          !errors.sendVia &&
          !errors.attachment,
        submitButtonText: 'Send',
      })}
    </BasePopup>
  );
};

SendSecureMessagePopup.propTypes = {
  patientId: PropTypes.number,
  onClose: PropTypes.func,
};

export default SendSecureMessagePopup;
